import gql from 'graphql-tag';
import {useMemo} from 'react';

import {useAccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {
  useAgentChatRequestedProjectWriteAccessQuery,
  useOrganizationFlagsQuery,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {type OrganizationFlags} from '../../../util/accounts/pricing';
import {jsonParseSafe} from '../../../util/json';

/**
 * Root-turn scope resolution
 *
 * Agent Chat can be opened while viewing a project the viewer can read without
 * being allowed to use for agent work. Before creating a root turn, this module
 * resolves one scope from authenticated, viewer-specific GraphQL data:
 *
 * 1. Use the requested entity/project only when the entity is a team the viewer
 *    has joined and `Project.readOnly` says this requester can write the project.
 * 2. Otherwise, use a team the viewer has joined under the selected organization:
 *    its configured default team when that team is present in the viewer's
 *    memberships, else any other team of the viewer's in that organization.
 *    Omit the project. Most organizations never configure a default team (it
 *    requires an admin to claim an email domain), so requiring an exact match
 *    on it would fall through to the viewer's personal entity for an org
 *    they're already a team member of -- which the viewer can't use ARIA from
 *    anyway once the personal entity's `create_turn` request comes back 403.
 * 3. Otherwise, use the viewer's default entity. Omit the project.
 *
 * `Project.readOnly` can be false for an open project owned by a team the viewer
 * has not joined, which is why project write access and membership are checked
 * independently before preserving the requested scope.
 *
 * Omitting the project selects WB Agent's default project. While project access
 * or organization settings are loading, the hook returns null and the client is
 * temporarily unscoped, which is also a valid server-default request. The client
 * is replaced with the resolved scope when the data arrives. Child turns do not
 * send scope; they inherit it from their parent on the server.
 */

export const AGENT_CHAT_REQUESTED_PROJECT_WRITE_ACCESS_QUERY = gql`
  query AgentChatRequestedProjectWriteAccess(
    $entityName: String!
    $projectName: String!
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      readOnly
    }
  }
`;

type AgentChatTeam = {
  id: string;
  name: string;
  isTeam: boolean;
  organizationId?: string;
};

type RootTurnScope = {
  entity: string;
  project?: string;
};

/** Choose where a new agent conversation is created from resolved inputs. */
function resolveRootTurnScope({
  requestedEntity,
  requestedProject,
  requestedProjectIsWritable,
  selectedOrganizationId,
  selectedOrganizationDefaultEntityId,
  viewerDefaultEntityName,
  viewerTeams,
}: {
  requestedEntity?: string;
  requestedProject?: string;
  requestedProjectIsWritable: boolean;
  selectedOrganizationId?: string;
  selectedOrganizationDefaultEntityId?: string;
  viewerDefaultEntityName?: string;
  viewerTeams: AgentChatTeam[];
}): RootTurnScope | null {
  const isMemberOfRequestedTeam = viewerTeams.some(
    team => team.isTeam && team.name === requestedEntity
  );
  if (
    requestedEntity &&
    requestedProject &&
    isMemberOfRequestedTeam &&
    requestedProjectIsWritable
  ) {
    return {entity: requestedEntity, project: requestedProject};
  }

  const selectedOrganizationTeams = viewerTeams.filter(
    team => team.isTeam && team.organizationId === selectedOrganizationId
  );
  const selectedOrganizationTeam =
    selectedOrganizationTeams.find(
      team => team.id === selectedOrganizationDefaultEntityId
    ) ?? selectedOrganizationTeams[0];
  if (selectedOrganizationTeam) {
    return {entity: selectedOrganizationTeam.name};
  }

  return viewerDefaultEntityName ? {entity: viewerDefaultEntityName} : null;
}

/** Resolve the scope used to create a root Agent Chat turn. */
export function useRootTurnScope(
  requestedEntity?: string,
  requestedProject?: string
): RootTurnScope | null {
  const viewer = useViewer();
  const {selectedAccount} = useAccountSelectorContext();
  const viewerTeams = useMemo(
    () =>
      viewer?.teams.edges.map(({node}) => ({
        id: node.id,
        name: node.name,
        isTeam: node.isTeam,
        organizationId: node.organizationId,
      })) ?? [],
    [viewer?.teams.edges]
  );
  const isMemberOfRequestedTeam = viewerTeams.some(
    team => team.isTeam && team.name === requestedEntity
  );

  const requestedProjectQuery = useAgentChatRequestedProjectWriteAccessQuery({
    variables: {
      entityName: requestedEntity ?? '',
      projectName: requestedProject ?? '',
    },
    skip: !requestedEntity || !requestedProject || !isMemberOfRequestedTeam,
  });
  const selectedOrganizationQuery = useOrganizationFlagsQuery({
    variables: {orgName: selectedAccount?.name ?? ''},
    skip: !selectedAccount?.name,
    fetchPolicy: 'cache-and-network',
  });

  const waitingForRequestedProject =
    !!requestedEntity &&
    !!requestedProject &&
    isMemberOfRequestedTeam &&
    requestedProjectQuery.loading &&
    !requestedProjectQuery.data;
  const waitingForSelectedOrganization =
    !!selectedAccount?.name &&
    selectedOrganizationQuery.loading &&
    !selectedOrganizationQuery.data;

  const selectedOrganizationFlags: OrganizationFlags =
    selectedOrganizationQuery.data?.organization?.flags != null
      ? jsonParseSafe(selectedOrganizationQuery.data.organization.flags)
      : {};

  return useMemo(() => {
    if (waitingForRequestedProject || waitingForSelectedOrganization) {
      return null;
    }

    return resolveRootTurnScope({
      requestedEntity,
      requestedProject,
      requestedProjectIsWritable:
        requestedProjectQuery.data?.project?.readOnly === false,
      selectedOrganizationId: selectedAccount?.id,
      selectedOrganizationDefaultEntityId:
        selectedOrganizationFlags.default_entity_id_for_domain_matching,
      viewerDefaultEntityName: viewer?.defaultEntity?.name,
      viewerTeams,
    });
  }, [
    requestedEntity,
    requestedProject,
    requestedProjectQuery.data?.project?.readOnly,
    selectedAccount?.id,
    selectedOrganizationFlags.default_entity_id_for_domain_matching,
    viewer?.defaultEntity?.name,
    viewerTeams,
    waitingForRequestedProject,
    waitingForSelectedOrganization,
  ]);
}
